import { useState, type ReactNode } from 'react';
import { audit, logs, type UrovenLogu, type ZaznamAuditu, type ZaznamLogu } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import {
  Badge,
  Button,
  Card,
  Notice,
  PageHeader,
  SectionTitle,
  type Tone,
} from '../components/ui.js';
import { IconChevronRight, IconRefresh } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data } from './common.js';

/*
 * Logy.
 *
 * Dva zoznamy, jeden tvar riadka. Prevadzkovy log odpoveda na otazku
 * „co brana robila a preco", audit na otazku „kto to urobil" - su to ine
 * otazky, preto su oddelene, ale citaju sa rovnako, preto vyzeraju
 * rovnako (poziadavka zadavatela 2026-09-18).
 *
 * Prevadzkovy log zije len v pamati; trvaly zaznam je uloha systemu,
 * ktory branu spusta (`docker logs`, `journalctl`). Audit je v databaze.
 */

const TON: Record<UrovenLogu, Tone> = {
  trace: 'neutral',
  debug: 'neutral',
  info: 'neutral',
  warn: 'warn',
  error: 'danger',
  fatal: 'danger',
};

const UROVNE: readonly { readonly hodnota: UrovenLogu; readonly popis: string }[] = [
  { hodnota: 'trace', popis: 'Všetko' },
  { hodnota: 'debug', popis: 'Ladenie a vyššie' },
  { hodnota: 'info', popis: 'Informácie a vyššie' },
  { hodnota: 'warn', popis: 'Varovania a chyby' },
  { hodnota: 'error', popis: 'Len chyby' },
];

/** Spolocny vzhlad ovladacieho prvku v hlavicke sekcie. */
const VYBER =
  'rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface-raised) px-3 py-2 text-sm text-(--color-text)';

/**
 * Jeden riadok zaznamu — spolocny pre oba zoznamy.
 *
 * Cas ma na sirsej obrazovke vlastny stlpec pevnej sirky, aby sa dali
 * zaznamy prebehnut ocami zhora nadol; na mobile sa zalomi nad spravu.
 * Podrobnosti su schovane, kym o ne clovek nepoziada - inak by zoznam
 * zaplavili udaje, ktore pri bezom citani nikto nepotrebuje.
 */
function Zaznam({
  cas,
  znacka,
  titul,
  popis,
  podrobnosti,
}: {
  readonly cas: string;
  readonly znacka: ReactNode;
  readonly titul: string;
  readonly popis?: string | undefined;
  readonly podrobnosti?: ReactNode;
}): ReactNode {
  const [rozbalene, setRozbalene] = useState(false);
  const datum = new Date(cas);

  return (
    <li className="border-b border-(--color-border) py-2.5 last:border-b-0">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
        <time
          dateTime={cas}
          title={datum.toLocaleString('sk-SK')}
          className="shrink-0 font-mono text-xs text-(--color-text-subtle) sm:w-20"
        >
          {datum.toLocaleTimeString('sk-SK')}
        </time>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {znacka}
            <span className="min-w-0 text-sm break-words text-(--color-text)">{titul}</span>
          </div>
          {popis !== undefined && popis !== '' && (
            <span className="text-xs break-words text-(--color-text-muted)">{popis}</span>
          )}
        </div>

        {podrobnosti !== undefined && (
          <button
            type="button"
            onClick={() => setRozbalene((r) => !r)}
            aria-expanded={rozbalene}
            className="inline-flex shrink-0 items-center gap-1 self-start text-xs font-medium text-(--color-accent)"
          >
            Podrobnosti
            <IconChevronRight
              className={`size-3.5 transition-transform ${rozbalene ? 'rotate-90' : ''}`}
            />
          </button>
        )}
      </div>

      {rozbalene && podrobnosti !== undefined && <div className="mt-2 sm:pl-23">{podrobnosti}</div>}
    </li>
  );
}

/**
 * Podrobnosti ako dvojice nazov — hodnota.
 *
 * Citatelnejsie nez surovy JSON: v praxi je v nich par kluco v typu
 * `deviceId` alebo `zariadeni`, a tie sa hladaju ocami, nie parserom.
 */
function Podrobnosti({ data }: { readonly data: Readonly<Record<string, unknown>> }): ReactNode {
  const polozky = Object.entries(data);
  if (polozky.length === 0) return null;

  return (
    <dl className="flex flex-col gap-1 rounded-(--radius-control) bg-(--color-surface-sunken) p-3">
      {polozky.map(([kluc, hodnota]) => (
        <div key={kluc} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
          <dt className="shrink-0 text-xs text-(--color-text-subtle) sm:w-40">{kluc}</dt>
          <dd className="min-w-0 font-mono text-xs break-all text-(--color-text-muted)">
            {typeof hodnota === 'string' ? hodnota : JSON.stringify(hodnota)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Detail auditu zo servera.
 *
 * Je to JSON, ale ulozeny ako text - a zapisat ho mohla aj starsia
 * verzia brany. Co sa rozobrat neda, ukaze sa tak, ako prislo.
 */
function detailAuditu(detail: string | null): Readonly<Record<string, unknown>> | string | null {
  if (detail === null || detail === '') return null;
  try {
    const data: unknown = JSON.parse(detail);
    if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
      return data as Record<string, unknown>;
    }
  } catch {
    // Nie je to JSON; ukaze sa ako text.
  }
  return detail;
}

export function Logs(): ReactNode {
  const { user } = useSession();

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Logy" detail="Čo brána robila a prečo." />
        <Notice tone="warn">
          Záznamy obsahujú IP adresy a podrobnosti o chybách, preto ich vidí len správca.
        </Notice>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Logy"
        detail="Dva pohľady: čo brána robila a kto do nej zasiahol. Tajomstvá sú z oboch odstránené."
      />

      <Audit />
      <PrevadzkovyLog />
    </div>
  );
}

/*
 * Audit citlivych operacii.
 *
 * Oddeleny od bezneho logu zamerne: log rotuje, drzi sa v pamati a miesa
 * sa s diagnostikou. Audit odpoveda na otazku „kto to urobil" aj o mesiac
 * - preto je v databaze a preto je tu vlastny zoznam. Je hore, lebo je
 * ho malo a cita sa castejsie.
 */

const POPIS_OPERACIE: Record<string, string> = {
  'auth.login': 'Prihlásenie',
  'auth.login.failed': 'Neúspešné prihlásenie',
  'auth.setup': 'Založenie prvého správcu',
  'user.create': 'Založený účet',
  'user.role.change': 'Zmenená rola účtu',
  'user.remove': 'Odobraný účet',
  'user.password.change': 'Zmena hesla',
  'update.request': 'Vyžiadaná aktualizácia',
  'tuya.cloud.sync': 'Stiahnutie kľúčov z Tuya IoT',
  'tuya.cloud.forget': 'Zabudnutý prístup k Tuya IoT',
  'tuya.cloud.autofetch': 'Automatika kľúčov (Tuya IoT)',
  'tuya.device.add': 'Ručne pridané zariadenie Tuya',
  'tuya.device.remove': 'Odobrané zariadenie Tuya',
  'tuya.key.set': 'Nastavený lokálny kľúč',
  'tuya.key.forget': 'Zabudnutý lokálny kľúč',
  'tuya.key.reveal': 'Zobrazený lokálny kľúč',
  'tuya.key.export': 'Export lokálnych kľúčov',
  'tuya.app.account.add': 'Pridaný účet aplikácie Tuya',
  'tuya.app.account.remove': 'Odobraný účet aplikácie Tuya',
  'tuya.app.sync': 'Synchronizácia účtu aplikácie Tuya',
  'tuya.app.import': 'Import zariadení z účtu aplikácie Tuya',
  'tuya.sharing.account.add': 'Prihlásený účet Tuya cez QR kód',
  'tuya.sharing.account.remove': 'Odobraný účet Tuya prihlásený QR kódom',
  'tuya.sharing.sync': 'Synchronizácia účtu cez QR kód',
  'tuya.sharing.account.autofetch': 'Automatika kľúčov (QR účet)',
  'tuya.app.account.autofetch': 'Automatika kľúčov (účet aplikácie)',
  'device.remove': 'Zmazané zariadenie',
  'diagnostics.matter': 'Diagnostika Matter',
  'matter.commission': 'Párovanie zariadenia Matter',
  'matter.decommission': 'Odobranie zariadenia Matter z fabriky',
  'system.restart': 'Reštart brány z rozhrania',
  'zigbee.permitJoin': 'Povolené párovanie Zigbee',
  'zigbee.availability.enable': 'Zapnuté sledovanie dostupnosti',
  'zigbee.bind': 'Vytvorená väzba Zigbee',
  'zigbee.firmware.update': 'Aktualizácia firmvéru Zigbee',
  'zigbee.networkmap': 'Zobrazená mapa siete Zigbee',
  'zigbee.channel': 'Zmena kanála Zigbee',
  'zigbee.node.rename': 'Premenovaný uzol Zigbee siete',
  'weather.location.set': 'Nastavená poloha pre počasie',
  'weather.location.forget': 'Zabudnutá poloha pre počasie',
  'loxone.miniserver.pair': 'Spárovaný Miniserver',
  'loxone.miniserver.unpair': 'Zrušené párovanie Miniservera',
  'loxone.mapping.create': 'Vytvorené mapovanie',
  'loxone.mapping.remove': 'Zrušené mapovanie',
  'loxone.template.download': 'Stiahnutá šablóna (obsahuje token)',
  'loxone.template.download.all': 'Stiahnuté všetky šablóny (obsahujú token)',
  'backup.create': 'Stiahnutá záloha',
  'backup.restore': 'Potvrdená obnova zo zálohy',
  'backup.restore.cancel': 'Zrušená čakajúca obnova',
  'radio.switch': 'Prepnuté USB rádio',
  'thread.network': 'Zmena siete Thread',
  'thread.state': 'Zapnutie/vypnutie Threadu',
  'thread.energy-scan': 'Meranie obsadenosti pásma',
  'thread.infra': 'Zmena rozhrania border routera',
};

/**
 * Ktore operacie stoja za zvyraznenie.
 *
 * Neuspesne prihlasenie a vydanie kluca nie su bezna prevadzka: prve
 * moze byt pokus o vlamanie, druhe vydava plnohodnotny pristup
 * k zariadeniu. V dlhom zozname sa maju dat najst bez citania.
 */
function tonOperacie(action: string): Tone {
  if (action === 'auth.login.failed') return 'danger';
  if (action.startsWith('tuya.key.') || action === 'backup.restore') return 'warn';
  if (action === 'tuya.sharing.account.add') return 'warn';
  // Zmena roly je zmena opravneni a prepnutie radia zhodi siet na
  // niekolko sekund - ani jedno nie je bezna prevadzka.
  if (action === 'user.role.change' || action === 'user.remove') return 'warn';
  if (action === 'radio.switch') return 'warn';
  // Zmena kanala Zigbee moze stat sparovanie batériovych zariadeni.
  if (action === 'zigbee.channel' || action === 'thread.network') return 'warn';
  return 'neutral';
}

function Audit(): ReactNode {
  const [operacia, setOperacia] = useState('');
  const nacitanie = useApi(
    (signal) => audit.list(operacia === '' ? undefined : operacia, signal),
    [operacia],
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>Citlivé operácie</SectionTitle>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={VYBER}
            value={operacia}
            onChange={(e) => setOperacia(e.target.value)}
            aria-label="Filtrovať podľa operácie"
          >
            <option value="">Všetky operácie</option>
            {Object.entries(POPIS_OPERACIE).map(([kluc, popis]) => (
              <option key={kluc} value={kluc}>
                {popis}
              </option>
            ))}
          </select>
          <Button
            onClick={nacitanie.reload}
            variant="ghost"
            icon={<IconRefresh className="size-4" />}
          >
            Obnoviť
          </Button>
        </div>
      </div>

      <p className="text-xs text-(--color-text-muted)">
        Kto do brány zasiahol. Zostáva v databáze aj po reštarte; kľúče ani heslá v ňom nie sú, len
        počty a identifikátory.
      </p>

      <Data nacitanie={nacitanie}>
        {(data) =>
          data.entries.length === 0 ? (
            <EmptyState
              title="Zatiaľ nič"
              detail={
                operacia === ''
                  ? 'Prihlásenia, párovania a zásahy do nastavení sa zapíšu sem.'
                  : 'Taká operácia sa zatiaľ nevykonala.'
              }
            />
          ) : (
            <Card className="px-4 py-1">
              <ul className="flex flex-col">
                {data.entries.map((z) => (
                  <AuditRiadok key={z.id} zaznam={z} />
                ))}
              </ul>
            </Card>
          )
        }
      </Data>
    </section>
  );
}

function AuditRiadok({ zaznam }: { readonly zaznam: ZaznamAuditu }): ReactNode {
  const detail = detailAuditu(zaznam.detail);

  /*
   * Podrobnosti ma kazdy zaznam auditu, aj ked ziadny detail neprisiel:
   * kto a coho sa to tykalo je prave to, na co sa clovek pri audite pyta.
   */
  const podrobnosti = (
    <Podrobnosti
      data={{
        Kto: zaznam.actor,
        Operácia: zaznam.action,
        ...(zaznam.subject === null ? {} : { 'Čoho sa týka': zaznam.subject }),
        Čas: new Date(zaznam.at).toLocaleString('sk-SK'),
        ...(detail === null ? {} : typeof detail === 'string' ? { Detail: detail } : detail),
      }}
    />
  );

  return (
    <Zaznam
      cas={zaznam.at}
      znacka={<Badge tone={tonOperacie(zaznam.action)}>{zaznam.actor}</Badge>}
      titul={POPIS_OPERACIE[zaznam.action] ?? zaznam.action}
      podrobnosti={podrobnosti}
    />
  );
}

/** Prevadzkovy log: co sa v brane deje, vratane diagnostiky. */
function PrevadzkovyLog(): ReactNode {
  const [uroven, setUroven] = useState<UrovenLogu>('info');
  const [hladanie, setHladanie] = useState('');
  const nacitanie = useApi(
    (signal) => logs.list({ level: uroven, search: hladanie, limit: 200 }, signal),
    [uroven, hladanie],
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionTitle>Prevádzkový log</SectionTitle>
        <Button
          onClick={nacitanie.reload}
          variant="ghost"
          icon={<IconRefresh className="size-4" />}
        >
          Obnoviť
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="flex items-center gap-2 text-sm text-(--color-text-muted)">
          Úroveň
          <select
            className={VYBER}
            value={uroven}
            onChange={(e) => setUroven(e.target.value as UrovenLogu)}
          >
            {UROVNE.map((u) => (
              <option key={u.hodnota} value={u.hodnota}>
                {u.popis}
              </option>
            ))}
          </select>
        </label>

        <input
          type="search"
          value={hladanie}
          onChange={(e) => setHladanie(e.target.value)}
          placeholder="Hľadať v správe aj v podrobnostiach"
          aria-label="Hľadať v logu"
          className={`${VYBER} flex-1 sm:max-w-sm`}
        />
      </div>

      <Data nacitanie={nacitanie}>
        {(data) =>
          data.logs.length === 0 ? (
            <EmptyState
              title="Žiadne záznamy"
              detail={
                hladanie === ''
                  ? 'Na tejto úrovni zatiaľ nič nepribudlo.'
                  : `Výrazu „${hladanie}" nezodpovedá žiadny záznam.`
              }
            />
          ) : (
            <Card className="px-4 py-1">
              <ul className="flex flex-col">
                {data.logs.map((z) => (
                  <LogRiadok key={z.seq} zaznam={z} />
                ))}
              </ul>
            </Card>
          )
        }
      </Data>

      <p className="text-xs text-(--color-text-subtle)">
        Držia sa len posledné záznamy a len v pamäti — po reštarte sú preč. Trvalý záznam nájdeš cez{' '}
        <code>docker logs</code> alebo <code>journalctl</code>.
      </p>
    </section>
  );
}

function LogRiadok({ zaznam }: { readonly zaznam: ZaznamLogu }): ReactNode {
  const maKontext = Object.keys(zaznam.context).length > 0;

  return (
    <Zaznam
      cas={zaznam.time}
      znacka={<Badge tone={TON[zaznam.level]}>{zaznam.level}</Badge>}
      titul={zaznam.msg}
      {...(maKontext ? { podrobnosti: <Podrobnosti data={zaznam.context} /> } : {})}
    />
  );
}
